import React from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  SortingState,
  ColumnDef,
  flexRender,
} from '@tanstack/react-table'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ArrowUpDown, Search, Trophy } from 'lucide-react'
import { cn } from '@/lib/utils'

interface AgentPerformance {
  id: string
  name: string
  email: string
  avatar?: string
  status: 'available' | 'busy' | 'offline'
  totalConversations: number
  totalMessages: number
  avgResponseTime: number
  satisfactionScore?: number
}

interface AgentPerformanceTableProps {
  data: AgentPerformance[]
  loading?: boolean
}

export default function AgentPerformanceTable({ data, loading = false }: AgentPerformanceTableProps) {
  const [sorting, setSorting] = React.useState<SortingState>([
    { id: 'totalConversations', desc: true }
  ])
  const [globalFilter, setGlobalFilter] = React.useState('')

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'available':
        return 'bg-brand-success text-white'
      case 'busy':
        return 'bg-brand-warning text-white'
      case 'offline':
        return 'bg-muted text-muted-foreground'
      default:
        return 'bg-muted text-muted-foreground'
    }
  }

  const formatResponseTime = (seconds: number) => {
    if (seconds < 60) return `${Math.round(seconds)}s`
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`
    return `${Math.round(seconds / 3600)}h`
  }

  const columns: ColumnDef<AgentPerformance>[] = [
    {
      id: 'rank',
      header: '',
      cell: ({ row }) => {
        const rank = row.index + 1
        return (
          <div className="flex items-center justify-center w-8">
            {rank <= 3 && (
              <Trophy className={cn(
                'h-5 w-5',
                rank === 1 && 'text-yellow-500',
                rank === 2 && 'text-gray-400',
                rank === 3 && 'text-orange-700'
              )} />
            )}
            {rank > 3 && <span className="text-muted-foreground text-sm">{rank}</span>}
          </div>
        )
      },
    },
    {
      accessorKey: 'name',
      header: 'Agent',
      cell: ({ row }) => {
        const agent = row.original
        const initials = agent.name
          .split(' ')
          .map(n => n[0])
          .join('')
          .toUpperCase()

        return (
          <div className="flex items-center gap-3">
            <Avatar>
              <AvatarImage src={agent.avatar} alt={agent.name} />
              <AvatarFallback className="bg-gradient-brand text-white text-sm">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="font-medium">{agent.name}</div>
              <div className="text-xs text-muted-foreground">{agent.email}</div>
            </div>
          </div>
        )
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.original.status
        return (
          <Badge className={cn('text-xs font-medium', getStatusColor(status))}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Badge>
        )
      },
    },
    {
      accessorKey: 'totalConversations',
      header: ({ column }) => {
        return (
          <button
            className="flex items-center gap-1 hover:text-foreground"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Conversations
            <ArrowUpDown className="h-3 w-3" />
          </button>
        )
      },
      cell: ({ row }) => {
        return <div className="font-medium">{row.original.totalConversations}</div>
      },
    },
    {
      accessorKey: 'totalMessages',
      header: ({ column }) => {
        return (
          <button
            className="flex items-center gap-1 hover:text-foreground"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Messages
            <ArrowUpDown className="h-3 w-3" />
          </button>
        )
      },
    },
    {
      accessorKey: 'avgResponseTime',
      header: ({ column }) => {
        return (
          <button
            className="flex items-center gap-1 hover:text-foreground"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Avg Response
            <ArrowUpDown className="h-3 w-3" />
          </button>
        )
      },
      cell: ({ row }) => {
        return <div>{formatResponseTime(row.original.avgResponseTime)}</div>
      },
    },
  ]

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Agent Performance Leaderboard</CardTitle>
          <CardDescription>Top performing agents ranked by conversations handled</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 animate-pulse">
                <div className="h-10 w-10 bg-muted rounded-full" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-1/3 bg-muted rounded" />
                  <div className="h-3 w-1/4 bg-muted rounded" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <CardTitle className="text-lg md:text-xl">Agent Performance Leaderboard</CardTitle>
            <CardDescription>Top performing agents ranked by conversations handled</CardDescription>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search agents..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    className="hover:bg-muted/50"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    No agents found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}
